import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert02Icon } from '@hugeicons/core-free-icons';
import { ModalAction } from '@overlayqa/shared/ui';
import { ApiError } from '../../lib/api/apiError';
import { NetworkError, NETWORK_ERROR_MESSAGE } from '../../lib/api/networkError';
import { isTimeoutError, REQUEST_TIMEOUT_MESSAGE } from '../../lib/api/requestTimeout';

interface LoadErrorProps {
  /** The affected content, as it should read in “We couldn’t load {resource}”. */
  resource?: string;
  error: Error;
  signedInEmail?: string | null;
  /** Return the request promise so progress and repeat attempts stay in sync. */
  onRetry: () => unknown;
  compact?: boolean;
  retrying?: boolean;
  /** Optional existing heading to focus after a successful keyboard retry. */
  recoveryFocusId?: string;
}

function recoveryMessage(resource: string, error: Error, offline: boolean, signedInEmail?: string | null): string {
  if (offline) return `Reconnect to the internet, then try again to load ${resource}.`;
  if (error instanceof ApiError) {
    if (error.status === 403) {
      const who = signedInEmail ? `You're signed in as ${signedInEmail}, and that account doesn't` : "This account doesn't";
      return `${who} have access to ${resource}. If you followed a shared link, sign in with the account it was shared with, then try again.`;
    }
    if (error.status === 401) return 'We couldn’t verify your sign-in. Sign in again, then try again.';
    if (error.status === 429) return 'There are too many requests right now. Wait a moment, then try again.';
    if (error.status === 503) return 'The service is unavailable right now. Try again, or come back later.';
    if (error.status >= 500) return 'Something went wrong on our side. Try again, or come back later.';
  }
  if (isTimeoutError(error)) return REQUEST_TIMEOUT_MESSAGE;
  if (error instanceof NetworkError) return NETWORK_ERROR_MESSAGE;
  return resource === 'billing information' ? 'Try again to view your plan and billing details.' : `Try again to load ${resource}.`;
}

/**
 * Shared in-page load failure. The explanation and recovery are always visible;
 * raw server messages and diagnostic codes are not customer-facing copy.
 * Status/code remain available as DOM metadata for fault-injection journeys.
 */
export function LoadError({ resource = 'issues', error, signedInEmail, onRetry, compact, retrying = false, recoveryFocusId }: LoadErrorProps) {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);
  const [pending, setPending] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const inFlight = useRef(false);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);
  const busy = pending || retrying;
  const retry = async () => {
    if (inFlight.current || retrying) return;
    inFlight.current = true;
    const restoreFocus = document.activeElement === button.current;
    setPending(true);
    setRetryFailed(false);
    try {
      const result = await onRetry();
      const failed = !!result && typeof result === 'object' && 'isError' in result && result.isError === true;
      setRetryFailed(failed);
      if (!failed && result && typeof result === 'object' && 'isSuccess' in result && result.isSuccess === true && restoreFocus && recoveryFocusId) {
        requestAnimationFrame(() => document.getElementById(recoveryFocusId)?.focus());
      }
    } catch {
      // The owner retains its error; keep recovery available after a rejection.
      setRetryFailed(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <div className={compact ? 'rounded-lg bg-gray-50 p-4 text-sm' : 'card-sm text-sm'} data-testid="load-error" data-error-status={error instanceof ApiError ? error.status : 'client'} data-error-code={error instanceof ApiError ? error.code : undefined}>
      <div className="flex items-start gap-3">
        <HugeiconsIcon icon={Alert02Icon} size={20} strokeWidth={2} className="text-gray-500 shrink-0 mt-1" aria-hidden="true" />
        <div className="min-w-0">
          <div role="alert" aria-atomic="true">
            <h3 className="text-base font-semibold text-gray-900">{offline ? 'You’re offline' : `We couldn’t load ${resource}`}</h3>
            <p data-testid="issues-load-error-detail" className="mt-2 text-sm text-gray-600 max-w-lg break-words">
              {recoveryMessage(resource, error, offline, signedInEmail)}
            </p>
          </div>
          <p className="mt-2 text-sm text-gray-600 empty:hidden" role="status" aria-live="polite">
            {busy ? `Trying to load ${resource}...` : retryFailed ? `Still unable to load ${resource}. You can email support for help.` : ''}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button ref={button} type="button" className={compact ? 'btn-secondary text-xs' : 'btn-primary'} onClick={() => void retry()} disabled={busy}>
              <ModalAction kind="retry">{busy ? 'Trying again...' : 'Try again'}</ModalAction>
            </button>
            <a className="font-medium text-primary-600 underline underline-offset-4 hover:text-primary-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" href="mailto:hello@overlayqa.com"><span className="text-cap-trim block">Email support</span></a>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Issues-flavored alias kept for the original call sites. */
export function IssuesLoadError(props: Omit<LoadErrorProps, 'resource' | 'compact'>) {
  return <LoadError {...props} resource="issues" />;
}

/** A failed refresh leaves the last successful list usable, with its selection. */
export function IssuesRefreshError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  return (
    <div className="alert-error mb-3 text-sm" role="alert">
      <p className="mb-2">Couldn't refresh issues. Showing the last loaded issues.</p>
      <button type="button" className="btn-secondary text-xs" onClick={onRetry} disabled={retrying}>
        <ModalAction kind="retry"> {retrying ? 'Retrying...' : 'Try again'}
      </ModalAction></button>
    </div>
  );
}
